import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const TrainingTypeSelector = forwardRef(({ onChangeStatus }, ref) => {
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [originalTypes, setOriginalTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Check if there are unsaved changes
  const hasChanges = JSON.stringify([...selectedTypes].sort()) !== JSON.stringify([...originalTypes].sort());

  // Notify parent about change status
  useEffect(() => {
    if (onChangeStatus) {
      onChangeStatus(hasChanges);
    }
  }, [hasChanges, onChangeStatus]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch available types
      const typesResponse = await fetch(`${API_BASE}/api/training-types`);
      if (typesResponse.ok) {
        const types = await typesResponse.json();
        setAvailableTypes(types);
      }

      // Fetch trainer's selected types
      const selectedResponse = await fetch(`${API_BASE}/api/trainer/training-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (selectedResponse.ok) {
        const data = await selectedResponse.json();
        const types = data.training_types || [];
        setSelectedTypes(types);
        setOriginalTypes(types);
      }
    } catch (error) {
      console.error('[TrainingTypeSelector] Error fetching training types:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to load training types', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleType = (typeName) => {
    setSelectedTypes(prev => {
      // Simple toggle logic for any type
      if (prev.includes(typeName)) {
        return prev.filter(t => t !== typeName);
      } else {
        return [...prev, typeName];
      }
    });
  };

  const handleSave = async () => {
    if (selectedTypes.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one training type',
        variant: 'destructive'
      });
      return false;
    }

    if (!hasChanges) {
      return true; // No changes to save
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/training-types`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          training_types: selectedTypes
        })
      });

      if (response.ok) {
        setOriginalTypes([...selectedTypes]);
        return true;
      } else {
        const error = await response.json();
        toast({ 
          title: 'Error', 
          description: error.detail || 'Failed to update training types', 
          variant: 'destructive' 
        });
        return false;
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to update training types', 
        variant: 'destructive' 
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleSave,
    hasChanges: () => hasChanges
  }));

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Loading training types...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
          <CheckCircle2 className="w-5 h-5" />
          Training Session Types
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Select how you provide training sessions
        </p>
      </div>
      <div className="p-4">
        {availableTypes.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <p>No training types available. Contact admin to set up training types.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {availableTypes.map((type) => (
              <div
                key={type.id}
                className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedTypes.includes(type.name)
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                }`}
                onClick={() => handleToggleType(type.name)}
              >
                <Checkbox
                  checked={selectedTypes.includes(type.name)}
                  onCheckedChange={() => handleToggleType(type.name)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>
                  {type.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{type.description}</div>
                  )}
                </div>
              </div>
            ))}

            {selectedTypes.length === 0 && (
              <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-700">
                ⚠️ Please select at least one training type to show on your profile
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedTypes.length > 0 ? (
                  <span>
                    <strong className="text-gray-900 dark:text-white">{selectedTypes.length}</strong> type{selectedTypes.length > 1 ? 's' : ''} selected
                    {hasChanges && <span className="text-amber-600 dark:text-amber-400 ml-2">(unsaved)</span>}
                  </span>
                ) : (
                  <span>No types selected</span>
                )}
              </div>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving || selectedTypes.length === 0}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

TrainingTypeSelector.displayName = 'TrainingTypeSelector';

export default TrainingTypeSelector;
